import { FC, useEffect, useMemo, useRef, useState } from 'react';

import { BuyerAuthSlideout } from 'components/common/AuthSlideout';
import { CloudinaryImage } from 'components/common/CloudinaryImage';
import { Slideout, SlideoutCommonProps, SubmitButtonWithArrow } from 'components/design-system/Slideout';
import { SHOULD_OPEN_REQUESTS_SLIDEOUT } from 'consts/storageKeys';
import { useAnalytics } from 'context/analytics';
import { useBezelUser } from 'context/bezelUser';
import { useModal } from 'context/modal/ModalContext';
import { usePurchaseRequest } from 'context/PurchaseRequest';
import { logPurchaseRequestStarted, logViewedRequestBoard } from 'lib/events';
import { setToStorage } from 'lib/storageHelpers';
import { ListingCondition, Model } from 'types';

import { EmptyRequestsTabContent } from './EmptyRequestTabContent';
import { getSplitPurchaseRequests } from './getSplitPurchaseRequests';
import { OpenRequestItem } from './OpenRequestItem';

import { TabSelector } from '../components/TabSelector';
import { RequestConditionSlideout } from '../RequestConditionSlideout';
import { RequestConversationSlideout } from '../RequestConversationSlideout';

type OpenRequestsSlideoutProps = {
  model?: Model;
} & SlideoutCommonProps;

export const OpenRequestsSlideout: FC<OpenRequestsSlideoutProps> = ({ model: modelFromProps, ...props }) => {
  const { closeModal, openModal } = useModal();
  const { user } = useBezelUser();
  const analytics = useAnalytics();
  const [selectedTab, setSelectedTab] = useState<ListingCondition>(ListingCondition.Unworn);
  const isLoggedViewedRequestBoard = useRef(false);

  const { model, setModel, purchaseRequests, resetRequest, requestOffers, refreshData } = usePurchaseRequest();

  useEffect(() => {
    if (isLoggedViewedRequestBoard.current) {
      return;
    }
    isLoggedViewedRequestBoard.current = true;
    logViewedRequestBoard(analytics);
  }, [analytics]);
  useEffect(() => {
    refreshData();
  }, [refreshData]);
  useEffect(() => {
    if (modelFromProps) {
      setModel(modelFromProps);
    }
  }, [modelFromProps, setModel]);
  const activeRequest = purchaseRequests.find(
    request => !!user?.buyerProfile && !!request.buyerProfile && request.buyerProfile.id === user?.buyerProfile?.id
  );
  const userHasActiveRequest = !!activeRequest;
  const openRequestConditionSlideout = () => {
    resetRequest();
    const onClick = () => setToStorage(SHOULD_OPEN_REQUESTS_SLIDEOUT, 'true');
    const nextSlideout = user ? (
      <RequestConditionSlideout closeModal={closeModal} />
    ) : (
      <BuyerAuthSlideout closeModal={closeModal} onClick={onClick} />
    );

    if (user) {
      logPurchaseRequestStarted(analytics, model);
    }
    openModal(nextSlideout, true);
  };
  const onYourRequestClick = () => {
    if (activeRequest) {
      openModal(<RequestConversationSlideout canGoBack request={activeRequest} closeModal={closeModal} />, true);
    }
  };

  const splitPurchaseRequests = useMemo(() => getSplitPurchaseRequests(purchaseRequests), [purchaseRequests]);

  useEffect(() => {
    if (
      splitPurchaseRequests[ListingCondition.Unworn].length === 0 &&
      splitPurchaseRequests[ListingCondition.Preowned].length > 0
    ) {
      setSelectedTab(ListingCondition.Preowned);
    }
  }, [splitPurchaseRequests]);

  return (
    <Slideout
      title='Open requests'
      submit={{
        textClassname: 'w-100',
        title: (
          <SubmitButtonWithArrow text={userHasActiveRequest ? 'View your request' : 'Submit a purchase request'} />
        ),
        onClick: userHasActiveRequest ? onYourRequestClick : openRequestConditionSlideout
      }}
      {...props}
    >
      <div className='w-100 pb-5 d-flex flex-column align-items-center'>
        <CloudinaryImage
          style={{ objectFit: 'cover' }}
          width={76}
          height={96}
          src={model?.images?.[0]?.optimizedUrl || 'model_placeholder.png'}
          alt={model?.displayName || 'Model image'}
        />
        <p className='mt-2 mb-1 text-uppercase fs-12px letter-spacing-08 opacity-60'>{model?.brand?.displayName}</p>
        <p className='fs-16px letter-spacing-007 text-center'>{model?.displayName}</p>
        <TabSelector selectedTab={selectedTab} setSelectedTab={setSelectedTab} splitData={splitPurchaseRequests} />
        <div className='w-100 mt-3' style={{ overflowY: 'auto' }}>
          {splitPurchaseRequests[selectedTab].length === 0 ? (
            <EmptyRequestsTabContent selectedTab={selectedTab} />
          ) : (
            splitPurchaseRequests[selectedTab].map(item => (
              <OpenRequestItem
                isYourRequest={!!item?.buyerProfile?.id && item.buyerProfile.id === user?.buyerProfile?.id}
                key={item.id}
                purchaseRequest={item}
                requestOffers={requestOffers.filter(({ purchaseRequest }) => purchaseRequest.id === item.id)}
              />
            ))
          )}
        </div>
      </div>
    </Slideout>
  );
};
